import { useEffect, useState } from "react";
import {
  Plus,
  Edit2,
  ChevronLeft,
  ChevronRight,
  Search,
  CheckCircle,
  XCircle,
  Loader2,
  Shield,
  ShieldOff,
  Users,
  UserCheck,
  UserX,
  Key,
  LayoutDashboard,
  Trash2,
  Calendar,
  Lock,
} from "lucide-react";
import axios from "axios";
import CreateStaffModal from "../../components/AdminSideComponents/Staff/CreateStaffModal";

// ─── Sidebar path → label map ─────────────────────────────────────────────────
const PATH_LABEL = {
  "/dashboard":   "Dashboard",
  "/staff":       "Staff",
  "/category":    "Category",
  "/product":     "Products",
  "/carrier":     "Carrier",
  "/job":         "Job Applications",
  "/testimonial": "Testimonial",
  "/faq":         "FAQ",
  "/inquiry":     "Inquiries",
  "/hero":        "Banner",
  "/blog":        "Blog",
};

// ─── Permission color chips ───────────────────────────────────────────────────
const PERM_COLORS = {
  dashboard:   { bg: "#f0fdf4", border: "#86efac", color: "#16a34a" },
  staff:       { bg: "#fdf4ff", border: "#e879f9", color: "#a21caf" },
  category:    { bg: "#eff6ff", border: "#93c5fd", color: "#2563eb" },
  product:     { bg: "#fff7ed", border: "#fdba74", color: "#ea580c" },
  carrier:     { bg: "#fdf2f8", border: "#f0abfc", color: "#c026d3" },
  application: { bg: "#f0fdfa", border: "#5eead4", color: "#0d9488" },
  admin:       { bg: "#fef2f2", border: "#fca5a5", color: "#dc2626" },
  enquiry:     { bg: "#fefce8", border: "#fde047", color: "#ca8a04" },
};

const getPermColor = (perm) => PERM_COLORS[perm] || { bg: "#f8fafc", border: "#cbd5e1", color: "#64748b" };

function PermChip({ perm }) {
  const c = getPermColor(perm);
  return (
    <span
      style={{ background: c.bg, borderColor: c.border, color: c.color }}
      className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border"
    >
      {perm.charAt(0).toUpperCase() + perm.slice(1)}
    </span>
  );
}

function StatusBadge({ isActive }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold
      ${isActive ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
      {isActive ? <><CheckCircle size={11} /> Active</> : <><XCircle size={11} /> Inactive</>}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  const accents = {
    indigo: "border-indigo-500 text-indigo-600 bg-indigo-50",
    emerald: "border-emerald-500 text-emerald-600 bg-emerald-50",
    red: "border-red-500 text-red-600 bg-red-50",
  };
  return (
    <div className={`bg-white p-5 rounded-xl border border-slate-200 shadow-sm border-l-4 ${accents[accent].split(" ")[0]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accents[accent].split(" ").slice(1).join(" ")}`}>
          <Icon size={20} />
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const Staff = () => {
  const [staff, setStaff]           = useState([]);
  const [modalOpen, setModalOpen]   = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [page, setPage]             = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading]       = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const itemsPerPage = 8;

  const fetchStaff = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/role/get`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStaff(res.data);
      console.log("first", res.data);
    } catch (error) {
      console.error("Error fetching roles:", error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStaff(); }, []);

  const handleCreate = () => { setSelectedRole(null); setModalOpen(true); };
  const handleEdit   = (role) => { setSelectedRole(role); setModalOpen(true); };

  const toggleStatus = async (id, current) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/role/${id}`, { isActive: !current });
      setStaff(prev => prev.map(r => r._id === id ? { ...r, isActive: !current } : r));
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this role?")) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/role/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("solarToken")}` },
      });
      setStaff(prev => prev.filter(r => r._id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  // Filter
  const filtered = staff.filter(r =>
    r.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort by createdAt desc
  const sorted = [...filtered].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  const totalPages = Math.ceil(sorted.length / itemsPerPage);
  const paginated  = sorted.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  // ── Loading ──
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-9 h-9 animate-spin text-emerald-600" />
          <p className="text-slate-500 font-medium">Loading staff roles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">

        {/* ── Page Header ──────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Staff Roles</h1>
            <p className="text-sm text-slate-500 mt-1">Manage staff roles and their module permissions</p>
          </div>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-semibold text-sm shadow-md shadow-emerald-200 hover:shadow-emerald-300 hover:-translate-y-0.5 transition-all duration-150"
          >
            <Plus size={18} />
            Add Staff Role
          </button>
        </div>

        {/* ── Stat Cards ───────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-7">
          <StatCard icon={Users}     label="Total Roles"    value={staff.length}                          accent="indigo"  />
          <StatCard icon={UserCheck} label="Active Roles"   value={staff.filter(r => r.isActive).length}  accent="emerald" />
          <StatCard icon={UserX}     label="Inactive Roles" value={staff.filter(r => !r.isActive).length} accent="red"     />
        </div>

        {/* ── Search Bar ───────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-5">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Search by role name…"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 transition-all"
            />
          </div>
        </div>

        {/* ── Table ────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">Permissions</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">Sidebar Pages</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginated.map((role) => {
                  const isExpanded = expandedRow === role._id;
                  const visiblePerms   = role.permissions?.slice(0, 3) || [];
                  const hiddenPermCnt  = (role.permissions?.length || 0) - 3;
                  const visiblePages   = role.sidebar?.slice(0, 2) || [];
                  const hiddenPagesCnt = (role.sidebar?.length || 0) - 2;

                  return (
                    <>
                      <tr key={role._id} className="hover:bg-slate-50/70 transition-colors">

                        {/* Role Name */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-200 border border-emerald-200 flex items-center justify-center text-emerald-700 font-bold text-base flex-shrink-0">
                              {role.name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-slate-900 text-sm">{role.name}</p>
                                {role.isSystem && (
                                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-500 border border-slate-200">
                                    <Lock size={9} /> System
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-slate-400 font-mono mt-0.5">{role._id.slice(-8)}</p>
                            </div>
                          </div>
                        </td>

                        {/* Permissions */}
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap items-center gap-1">
                            {visiblePerms.map(p => <PermChip key={p} perm={p} />)}
                            {hiddenPermCnt > 0 && (
                              <button
                                onClick={() => setExpandedRow(isExpanded ? null : role._id)}
                                className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-slate-100 border border-slate-200 text-slate-500 hover:bg-slate-200 transition-colors"
                              >
                                +{hiddenPermCnt} more
                              </button>
                            )}
                            {(!role.permissions || role.permissions.length === 0) && (
                              <span className="text-xs text-slate-400">None</span>
                            )}
                          </div>
                        </td>

                        {/* Sidebar Pages */}
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap items-center gap-1">
                            {visiblePages.map(s => (
                              <span key={s._id || s.path}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-emerald-50 border border-emerald-200 text-emerald-700">
                                <LayoutDashboard size={9} />
                                {PATH_LABEL[s.path] || s.path}
                              </span>
                            ))}
                            {hiddenPagesCnt > 0 && (
                              <button
                                onClick={() => setExpandedRow(isExpanded ? null : role._id)}
                                className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-slate-100 border border-slate-200 text-slate-500 hover:bg-slate-200 transition-colors"
                              >
                                +{hiddenPagesCnt} more
                              </button>
                            )}
                            {(!role.sidebar || role.sidebar.length === 0) && (
                              <span className="text-xs text-slate-400">None</span>
                            )}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4">
                          <button
                            onClick={() => !role.isSystem && toggleStatus(role._id, role.isActive)}
                            disabled={role.isSystem}
                            className="transition-transform hover:scale-105 disabled:cursor-not-allowed disabled:opacity-60"
                            title={role.isSystem ? "System roles cannot be toggled" : "Toggle status"}
                          >
                            <StatusBadge isActive={role.isActive} />
                          </button>
                        </td>

                        {/* Created */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1.5 text-xs text-slate-400">
                            <Calendar size={12} />
                            {formatDate(role.createdAt)}
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEdit(role)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg hover:bg-emerald-50 hover:text-emerald-700 border border-transparent hover:border-emerald-200 transition-all text-xs font-medium"
                            >
                              <Edit2 size={13} /> Edit
                            </button>
                            {!role.isSystem && (
                              <button
                                onClick={() => handleDelete(role._id)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-500 rounded-lg hover:bg-red-50 hover:text-red-600 border border-transparent hover:border-red-200 transition-all text-xs font-medium"
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* ── Expanded detail row ── */}
                      {isExpanded && (
                        <tr key={`${role._id}-expand`} className="bg-slate-50/80">
                          <td colSpan={6} className="px-6 py-4">
                            <div className="grid grid-cols-2 gap-6">
                              {/* All permissions */}
                              <div>
                                <p className="text-[10.5px] font-bold uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
                                  <Key size={11} /> All Permissions ({role.permissions?.length || 0})
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                  {role.permissions?.map(p => <PermChip key={p} perm={p} />) || <span className="text-xs text-slate-400">None</span>}
                                </div>
                              </div>
                              {/* All sidebar pages */}
                              <div>
                                <p className="text-[10.5px] font-bold uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
                                  <LayoutDashboard size={11} /> All Sidebar Pages ({role.sidebar?.length || 0})
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                  {role.sidebar?.map(s => (
                                    <span key={s._id || s.path}
                                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-50 border border-emerald-200 text-emerald-700">
                                      {PATH_LABEL[s.path] || s.path}
                                    </span>
                                  )) || <span className="text-xs text-slate-400">None</span>}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => setExpandedRow(null)}
                              className="mt-3 text-xs text-slate-400 hover:text-slate-600 transition-colors"
                            >
                              ↑ Collapse
                            </button>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {paginated.length === 0 && (
            <div className="text-center py-16">
              <div className="mx-auto w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                <Shield className="text-slate-400" size={28} />
              </div>
              <h3 className="text-base font-semibold text-slate-900 mb-1">No roles found</h3>
              <p className="text-sm text-slate-500 mb-5">
                {searchTerm ? "Try a different search term" : "Create your first staff role to get started"}
              </p>
              {!searchTerm && (
                <button
                  onClick={handleCreate}
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors"
                >
                  <Plus size={16} /> Add Staff Role
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Pagination ───────────────────────────────────────── */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-5 px-5 py-4 bg-white rounded-xl border border-slate-200 shadow-sm">
            <p className="text-sm text-slate-500">
              Showing <span className="font-semibold text-slate-700">{(page - 1) * itemsPerPage + 1}</span>–
              <span className="font-semibold text-slate-700">{Math.min(page * itemsPerPage, sorted.length)}</span> of{" "}
              <span className="font-semibold text-slate-700">{sorted.length}</span> roles
            </p>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage(p => p - 1)} disabled={page === 1}
                className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>

              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pn;
                if (totalPages <= 5)          pn = i + 1;
                else if (page <= 3)           pn = i + 1;
                else if (page >= totalPages - 2) pn = totalPages - 4 + i;
                else                          pn = page - 2 + i;
                return (
                  <button
                    key={i} onClick={() => setPage(pn)}
                    className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                      page === pn
                        ? "bg-emerald-600 text-white shadow-md shadow-emerald-200"
                        : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {pn}
                  </button>
                );
              })}

              <button
                onClick={() => setPage(p => p + 1)} disabled={page === totalPages}
                className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Modal ────────────────────────────────────────────── */}
      <CreateStaffModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); fetchStaff(); }}
        editData={selectedRole}
      />
    </div>
  );
};

export default Staff;